import Calendar from 'react-calendar';
import React, { forwardRef, useEffect, useRef, useState } from "react";
import style from "./DatePicker.module.css";
import Button from '../Button/Button';
import IconCalendar from '@/components/icons/IconCalendar';
import Flex from '@/components/Flex/Flex';
import Flyout from '@/components/Flyout';
import Divider from '@/components/Divider/Divider';

interface DatePickerProps {
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: React.ReactNode;
  className?: string;

  minDate?: Date;
  maxDate?: Date;

  value: Date;
  onChange: (value: Date) => void;

  showClearButton?: boolean;
}

const DatePicker = forwardRef<any, DatePickerProps>((props: DatePickerProps, forwardRef) => {
  const { id, value, className, onChange, disabled, invalid, placeholder = "Select a date", showClearButton } = props;

  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const updateRefs = el => {
    buttonRef.current = el;
    if (forwardRef) {
      if (forwardRef instanceof Function) {
        forwardRef(el);
      } else {
        forwardRef.current = el;
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const outsideCalendar = calendarRef.current && !calendarRef.current.contains(e.target as Node);
      const outsideButton = buttonRef.current && !buttonRef.current.contains(e.target as Node);
      if (outsideCalendar && outsideButton) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={updateRefs}
      id={id}
      className={`
        ${style.datePicker}
        ${!!className ? className : ""}
      `}
    >
      <Button
        block
        className={`${style.datePickerButton} ${invalid ? style.invalid : ''}`}
        disabled={disabled}
        type="secondary"
        onClick={(e) => {
          e.stopPropagation(); // so the event listener above doesn't fire.
          setCalendarOpen(!calendarOpen);
        }}
      >
        <Flex className={style.buttonContent} align="center" gap={2}>
          <IconCalendar size={16} />
          {value && value.toLocaleDateString()}
          {(!value && placeholder) && placeholder}
        </Flex>
      </Button>
      {calendarOpen && (
        <Flyout ref={calendarRef} className={style.calendarContainer} matchWidth={false}>
          <Calendar
            returnValue={"start"} // 'onChange' will return a single 'Date' always instead of a range
            className={style.calendar}
            value={value}
            onChange={v => {
              onChange(v as Date);
              setCalendarOpen(false);
            }}
          />
          {showClearButton && (
            <Flex direction="column" gap={2}>
              <Divider margin="0 0 0.5rem 0"/>
              <Button
                type="text"
                onClick={v => {
                  onChange(null);
                  setCalendarOpen(false);
                }}
              >
                Clear Date
              </Button>
            </Flex>
          )}
        </Flyout>
      )}
    </div>
  );
});

export default DatePicker;